import { useMemo, useEffect, useState } from 'react';
import { Badge, Col, Form, Row } from 'antd';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button } from 'components';
import { DeleteIcon, EditIcon } from 'icons';
import { useTranslation } from 'react-i18next';
import { EyeOutlined } from '@ant-design/icons';
import FacilityExtrastockOffersApi from 'libs/api/facility/extrastockOffersApi';
import { useApi } from 'hooks/useApi';
import OffersAdapter from './offers.adapter';
import { useRecoilValue } from 'recoil';
import { getShops, getInactiveShops } from 'hooks/useShops';
import { getPage, getShoppingCenters } from 'hooks/useUser';
import { FormValuesAdapter } from './offers.adapter';
import UIHelper from 'helpers/UIHelper';
import ErrorHelper from 'helpers/ErrorHelper';
import { useNavigate } from 'hooks/useNavigate';
import FacilityExtrastocksApi from 'libs/api/facility/extrastocksApi';
import moment from 'moment';

const api = new FacilityExtrastockOffersApi();

const statusColors = {
  refused: 'red',
  in_progress: 'orange',
  confirmed: 'green',
  status_new: 'yellow',
  status_expired: 'black',
  approve: 'green',
};

const useExtrastockOffers = (isMainPage = true, isEdit) => {
  const { t } = useTranslation();
  let { id } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const shops = useRecoilValue(getShops);
  const inactiveShops = useRecoilValue(getInactiveShops);
  const shoppingCenters = useRecoilValue(getShoppingCenters);
  const pages = useRecoilValue(getPage);
  const [extrastockParams, setExtrastockParams] = useState({
    company: '',
    date_from: '',
    date_to: '',
  });
  const [extrastocks, setExtrastocks] = useState([]);
  const [form] = Form.useForm();
  const {
    data,
    onChangeTableFilter,
    loading,
    updateInProcess,
    handleDownloadExcel,
    singleItem,
    setUpdateInProcess,
    onDelete,
    setSingleItem,
  } = useApi({
    resourceKey: 'extrastock_offers',
    singleResourceKey: 'extrastock_offer',
    api: api,
    isMainPage,
    adapter: OffersAdapter,
  });

  const contractType = useMemo(
    () => ({
      short_term: t('Short term'),
      long_term: t('Long term'),
    }),
    [t],
  );

  const statusTypes = useMemo(
    () => ({
      refused: t('Rifiutato'),
      in_progress: t('Presa in carico'),
      confirmed: t('Comfermato'),
      status_new: t('Nuovo'),
      status_expired: t('Expired'),
      approve: t('Approvato'),
    }),
    [t],
  );

  const columns = useMemo(() => {
    return [
      {
        title: t('Outlet Village'),
        dataIndex: 'shopping_centers.title',
        fixed: 'left',
        sorter: () => { },
      },
      {
        title: t('Insegna'),
        dataIndex: 'companies.title',
        sorter: () => { },
      },
      {
        title: t('Unità'),
        dataIndex: 'extrastocks.title',
        sorter: () => { },
      },
      {
        title: t('Tipologia contratto'),
        dataIndex: 'contract_type',
        sorter: () => { },
        render: (data) => contractType[data],
      },

      {
        title: t('Data inizio'),
        dataIndex: 'date_from',
        sorter: () => { },
      },
      {
        title: t('Data fine'),
        dataIndex: 'date_to',
        sorter: () => { },
      },
      {
        title: t('Stato'),
        dataIndex: 'status',
        sorter: () => { },
        render: (data, row) => (
          <Badge color={statusColors[data]} text={statusTypes[data]} />
        ),
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 180,
        fixed: 'right',
        render: (data, row) =>
          (pages?.ability?.can_read !== false ||
            row.can_read !== false ||
            row.can_update ||
            row.can_approve ||
            row.can_destroy !== false) && (
            <Row
              gutter={16}
              justify={'end'}
              onClick={(e) => e.stopPropagation()}
            >
              {(pages?.ability?.can_read !== false ||
                row.can_read !== false) && (
                <Col>
                  <Link to={`${pathname}/${row.id}`}>
                    <Button type={'secondary'} icon={<EyeOutlined />} />
                  </Link>
                </Col>
              )}
              {(row.can_update || row.can_approve) && (
                <Col>
                  <Link to={`${pathname}/${row.id}/edit`}>
                    <Button type={'secondary'} icon={<EditIcon />} />
                  </Link>
                </Col>
              )}
              {row.can_destroy !== false && (
                <Col>
                  <Link to={`${pathname}/${row.id}/delete`}>
                    <Button type={'red'} icon={<DeleteIcon />} />
                  </Link>
                </Col>
              )}
            </Row>
          ),
      },
    ];
  }, [pathname, contractType, pages?.ability?.can_read, statusTypes]);
  const filters = useMemo(
    () => [
      {
        title: t('Outlet Village'),
        type: 'select',
        name: 'by_shopping_center',
        values: shoppingCenters.map((i) => ({ value: i.id, title: i.title })),
      },
      {
        title: t('Insegna'),
        type: 'select',
        name: 'by_company',
        isSearch: true,
        values: shops,
        inactiveValues: inactiveShops,
      },
      {
        title: t('Titolo'),
        type: 'input',
        name: 'by_title',
        isActiveSearch: true,
      },

      {
        title: t('Tipologia contratto'),
        type: 'select',
        name: 'by_contract_type',
        values: Object.entries(contractType).map((i) => ({
          value: i[0],
          title: i[1],
        })),
      },
    ],
    [t, shoppingCenters, shops, inactiveShops, contractType],
  );

  const handleCreateOrUpdate = async (values) => {
    setUpdateInProcess(true);
    try {
      const formData = FormValuesAdapter(values);
      if (!id) {
        await api.createOffer(formData);
        navigate(UIHelper.getListRoute(pathname, 1));
      } else {
        await api.updateOffer(id, formData);
        navigate(UIHelper.getListRoute(pathname));
      }
    } catch (e) {
      const errors = e.response.data || [];
      form.setFields(
        ErrorHelper.getErrorsForForm(errors, {
          company: 'company_id',
        }),
      );
    } finally {
      setUpdateInProcess(false);
    }
  };

  useEffect(() => {
    (async () => {
      if (!isMainPage) {
        try {
          if (
            extrastockParams.company &&
            extrastockParams.date_from &&
            extrastockParams.date_to
          ) {
            const {
              data: { extrastocks = [] },
            } = await api.getExtrastocks({
              per: 1000,
              ...extrastockParams,
            });
            setExtrastocks(extrastocks);
          }
        } catch (e) {
          console.log(e);
        }
      }
    })();
  }, [extrastockParams, isMainPage]);

  const onChangeExtrastockGetFields = (changedFields) => {
    if (changedFields[0]) {
      const { value, name } = changedFields[0];
      if (['date_from', 'date_to', 'company_id'].includes(name[0])) {
        if (name[0] === 'company_id') {
          setExtrastockParams((s) => ({
            ...s,
            company: value,
          }));
        } else {
          setExtrastockParams((s) => ({
            ...s,
            [name[0]]: value,
          }));
        }
      }
    }
  };

  useEffect(() => {
    (async () => {
      if (id) {
        try {
          if (isEdit) {
            let fieldsValues = [];
            for (let key in singleItem) {
              switch (key) {
                case 'date_from':
                case 'date_to': {
                  fieldsValues.push({
                    name: key,
                    value: moment(singleItem[key]),
                  });
                  break;
                }
                case 'offer_date': {
                  fieldsValues.push({
                    name: key,
                    value: moment(singleItem[key], 'YYYY-MM-DD HH:mm'),
                  });
                  break;
                }
                case 'company': {
                  fieldsValues.push({
                    name: 'company_id',
                    value: singleItem[key]?.id,
                  });
                  break;
                }
                case 'extrastock': {
                  fieldsValues.push({
                    name: 'extrastock_id',
                    value: singleItem[key]?.id,
                  });
                  break;
                }
                case 'contract_file':
                case 'end_rent_file':
                case 'rent_file':
                case 'hot_file_signed': {
                  break;
                }
                default: {
                  fieldsValues.push({
                    name: key,
                    value: singleItem[key],
                  });
                }
              }
            }
            form.setFields(fieldsValues);
          }
        } catch (e) {
          console.log(e);
        }
      }
    })();
  }, [singleItem]);

  const handleDeleteFile = async (data) => {
    setUpdateInProcess(true);
    try {
      const {
        file: { id },
        param,
      } = data;
      await api.deleteFile(singleItem.id, id);
      setSingleItem((s) => ({
        ...s,
        [param]: null,
      }));
    } catch (e) {
      console.log(e);
    } finally {
      setUpdateInProcess(false);
    }
  };

  const handleApprove = async (values, paramKey, backCount) => {
    const data = {
      email_message: values.description,
      [values.status]: true,
    };
    setUpdateInProcess(true);
    try {
      await api.approve(id, data);
      navigate(UIHelper.getListRoute(pathname, backCount));
    } catch (e) {
      console.log(e);
      const errors = e.response.data || [];
      form.setFields(ErrorHelper.getErrorsForForm(errors, {}));
    } finally {
      setUpdateInProcess(false);
    }
  };

  return {
    columns,
    data,
    filters,
    onChangeTableFilter,
    loading,
    form,
    shops,
    inactiveShops,
    contractType,
    statusTypes,
    extrastocks,
    updateInProcess,
    handleDownloadExcel,
    handleCreateOrUpdate,
    onChangeExtrastockGetFields,
    onDelete,
    singleItem,
    handleDeleteFile,
    handleApprove,
  };
};

export default useExtrastockOffers;
